import gql from 'graphql-tag'

// Asset fragments
export const FRAGMENT_MEDIA = gql`
  fragment Media on Media {
    id
    alt
    fullPath # same as: prefix + filename
    width
    height
    focalX
    focalY
  }
`
export const FRAGMENT_PORTRAIT = gql`
  fragment Portrait on Portrait {
    __typename
    id
    fullPath # same as: prefix + filename
    alt
    width
    height
    focalX
    focalY
  }
`
export const FRAGMENT_ICON = gql`
  fragment Icon on Icon {
    __typename
    id
    url
  }
`
export const FRAGMENT_LOGO = gql`
  fragment Logo on Logo {
    __typename
    id
    url
    name
    height
    width
  }
`

// Header / Footer - fragments

const FRAGMENT_NAVIGATION_ITEM = gql`
  fragment NavigationItem on NavigationItem {
    __typename
    id
    label
    target {
      relationTo
      value {
        ... on Page {
          slug
        }
        ... on Project {
          slug
        }
        ... on Publication {
          slug
        }
      }
    }
  }
`

const FRAGMENT_NAVIGATION_PARENT_ITEM = gql`
  ${FRAGMENT_NAVIGATION_ITEM}
  fragment NavigationParentItem on NavigationParentItem {
    __typename
    id
    label
    items {
      ...NavigationItem
    }
  }
`

const FRAGMENT_ICON_LINK = gql`
  ${FRAGMENT_ICON}
  fragment IconLink on IconLink {
    __typename
    id
    label
    url
    icon {
      ...Icon
    }
  }
`

const FRAGMENT_EXTERNAL_LINK = gql`
  fragment ExternalLink on ExternalLink {
    __typename
    id
    label
    url
    targetSiteLanguage
  }
`

const FRAGMENT_LANGUAGE_ITEM = gql`
  fragment LanguageItem on LanguageItem {
    __typename
    id
    language
    label
    abbr
  }
`

export const FRAGMENT_HEADER = gql`
  ${FRAGMENT_LOGO}
  ${FRAGMENT_NAVIGATION_ITEM}
  ${FRAGMENT_NAVIGATION_PARENT_ITEM}

  fragment Header on Header {
    logo {
      ...Logo
    }
    navigationItems {
      ...NavigationItem
      ...NavigationParentItem
    }
    cta {
      ...NavigationItem
    }
  }
`

export const FRAGMENT_FOOTER = gql`
  ${FRAGMENT_LOGO}
  ${FRAGMENT_ICON_LINK}
  ${FRAGMENT_EXTERNAL_LINK}
  ${FRAGMENT_NAVIGATION_ITEM}
  ${FRAGMENT_LANGUAGE_ITEM}

  fragment Footer on Footer {
    logo {
      ...Logo
    }

    socialTitle
    socialLinks {
      ...IconLink
    }

    contactTitle
    contactAddress

    sitemapTitle
    sitemapItems {
      ...ExternalLink
    }

    subscribeTitle

    bottomLineLinks {
      ...NavigationItem
    }
    copyright
    languages {
      ...LanguageItem
    }

    cookieMessage
  }
`

// Core fragments

const FRAGMENT_SECTION_DESIGN = gql`
  fragment SectionDesign on SectionDesign {
    background
    innerShadow
    containerWidth
    padding {
      top
      bottom
    }
  }
`

const FRAGMENT_IMAGE_OPTIONS = gql`
  fragment ImageOptions on ImageOptions {
    ratio
    objectFit
  }
`

export const FRAGMENT_PUBLICATION_CATEGORY = gql`
  fragment PublicationCategory on PublicationCategory {
    id
    slug
    label
  }
`

export const FRAGMENT_PROJECT_CATEGORY = gql`
  fragment ProjectCategory on ProjectCategory {
    id
    slug
    label
  }
`

export const FRAGMENT_COUNTRY = gql`
  fragment Country on Country {
    id
    code
    name
  }
`

// Link and CTA fragments
const FRAGMENT_LINK_TARGET = gql`
  fragment LinkTarget on LinkTarget {
    enabled
    type
    internal {
      relationTo
      value {
        ... on Page {
          slug
          title
        }
        ... on Project {
          slug
          title
        }
        ... on Publication {
          slug
          title
        }
      }
    }
    url
    email
    asset {
      url
    }
    filter {
      relationTo
      value {
        ... on ProjectCategory {
          slug
        }
        ... on PublicationCategory {
          slug
        }
      }
    }
  }
`

const FRAGMENT_LINK_TARGET_OPTIONAL = gql`
  fragment LinkTargetOptional on LinkTargetOptional {
    enabled
    type
    internal {
      relationTo
      value {
        ... on Page {
          slug
          title
        }
        ... on Project {
          slug
          title
        }
        ... on Publication {
          slug
          title
        }
      }
    }
    url
    email
    asset {
      url
    }
    filter {
      relationTo
      value {
        ... on ProjectCategory {
          slug
        }
        ... on PublicationCategory {
          slug
        }
      }
    }
  }
`

// Component fragments
const FRAGMENT_CMP_CTA = gql`
  ${FRAGMENT_ICON}
  ${FRAGMENT_LINK_TARGET}

  fragment CmpCta on CmpCta {
    __typename
    id
    label
    icon {
      ...Icon
    }
    appearance
    target {
      ...LinkTarget
    }
  }
`

const FRAGMENT_CMP_TITLE = gql`
  fragment CmpTitle on CmpTitle {
    __typename
    id
    title
  }
`

const FRAGMENT_CMP_RICH_TEXT = gql`
  fragment CmpRichText on CmpRichText {
    __typename
    id
    richText
  }
`

const FRAGMENT_CMP_IMAGE = gql`
  ${FRAGMENT_MEDIA}
  ${FRAGMENT_IMAGE_OPTIONS}

  fragment CmpImage on CmpImage {
    __typename
    id
    image {
      ...Media
    }
    caption
    imageOptions {
      ...ImageOptions
    }
  }
`

const FRAGMENT_CMP_VIDEO = gql`
  fragment CmpVideo on CmpVideo {
    __typename
    id
    type
    videoId
    width
    height
    caption
  }
`

const FRAGMENT_CMP_TESTIMONIAL = gql`
  ${FRAGMENT_PORTRAIT}
  fragment CmpTestimonial on CmpTestimonial {
    __typename
    id
    portrait {
      ...Portrait
    }
    name
    description
    text
  }
`

const FRAGMENT_CMP_IMG_CAROUSEL = gql`
  ${FRAGMENT_MEDIA}
  ${FRAGMENT_IMAGE_OPTIONS}

  fragment CmpImgCarousel on CmpImgCarousel {
    __typename
    id
    images {
      ...Media
    }
    interval
    imageOptions {
      ...ImageOptions
    }
  }
`

const FRAGMENT_CMP_LATEST_NEWS = gql`
  ${FRAGMENT_PUBLICATION_CATEGORY}

  fragment CmpLatestNews on CmpLatestNews {
    __typename
    id
    count
    categories {
      ...PublicationCategory
    }
  }
`

const FRAGMENT_CMP_NEWSLETTER_SUBSCRIPTION = gql`
  fragment CmpNewsletterSubscription on CmpNewsletterSubscription {
    __typename
    id
    inputAppearance
  }
`

// Card fragments
export const FRAGMENT_PUBLICATION_CARD_ITEM = gql`
  ${FRAGMENT_MEDIA}
  ${FRAGMENT_PUBLICATION_CATEGORY}

  fragment PublicationCardItem on Publication {
    __typename
    id
    slug
    title
    excerpt
    date
    image {
      ...Media
    }
    categories {
      ...PublicationCategory
    }
  }
`

export const FRAGMENT_CARD_PUBLICATION = gql`
  ${FRAGMENT_PUBLICATION_CARD_ITEM}

  fragment CardPublication on CardPublication {
    __typename
    id
    item {
      ...PublicationCardItem
    }
  }
`

export const FRAGMENT_PROJECT_CARD_ITEM = gql`
  ${FRAGMENT_MEDIA}
  ${FRAGMENT_PROJECT_CATEGORY}

  fragment ProjectCardItem on Project {
    __typename
    id
    slug
    title
    excerpt
    image {
      ...Media
    }
    categories {
      ...ProjectCategory
    }
  }
`

export const FRAGMENT_CARD_PROJECT = gql`
  ${FRAGMENT_PROJECT_CARD_ITEM}

  fragment CardProject on CardProject {
    __typename
    id
    item {
      ...ProjectCardItem
    }
  }
`
const FRAGMENT_CARD_IMAGE_TEXT = gql`
  ${FRAGMENT_MEDIA}
  ${FRAGMENT_IMAGE_OPTIONS}
  ${FRAGMENT_LINK_TARGET_OPTIONAL}

  fragment CardImageText on CardImageText {
    __typename
    id
    title
    text
    alignment
    image {
      ...Media
    }
    imageOptions {
      ...ImageOptions
    }
    linkLabel
    target {
      ...LinkTargetOptional
    }
  }
`

const FRAGMENT_CARD_FILE_DOWNLOAD = gql`
  ${FRAGMENT_MEDIA}

  fragment CardFileDownload on CardFileDownload {
    __typename
    id
    title
    image {
      ...Media
    }
    file {
      url
      filename
    }
  }
`

const FRAGMENT_CARD_SWISS_BILL_QR_CODE_DONATION = gql`
  fragment CardSwissBillQrCodeDonation on CardSwissBillQrCodeDonation {
    __typename
    id
    title
    text
    topic
    creditors
  }
`

export const FRAGMENTS_CARDS = gql`
  ${FRAGMENT_CARD_PUBLICATION}
  ${FRAGMENT_CARD_PROJECT}
  ${FRAGMENT_CARD_IMAGE_TEXT}
  ${FRAGMENT_CARD_FILE_DOWNLOAD}
  ${FRAGMENT_CARD_SWISS_BILL_QR_CODE_DONATION}
`

// Layout fragments

const FRAGMENT_LAYOUT_ACCORDION = gql`
  fragment AccordionItem on AccordionItem {
    id
    title
    text
  }
  fragment LayoutAccordion on LayoutAccordion {
    __typename
    id
    logicalType
    columnCount
    accordionItems {
      ...AccordionItem
    }
  }
`

const FRAGMENT_LAYOUT_GALLERY = gql`
  ${FRAGMENT_MEDIA}

  fragment GalleryItem on GalleryItem {
    __typename
    id
    image {
      ...Media
    }
    caption
  }
  fragment LayoutGallery on LayoutGallery {
    __typename
    id
    galleryItemsColumnCount
    galleryItems {
      ...GalleryItem
    }
  }
`

const FRAGMENT_LAYOUT_CARDS_GRID = gql`
  ${FRAGMENTS_CARDS}

  fragment LayoutCardsGrid on LayoutCardsGrid {
    __typename
    id
    cardsColumnCount
    cards {
      ...CardPublication
      ...CardProject
      ...CardImageText
      ...CardFileDownload
      ...CardSwissBillQrCodeDonation
    }
  }
`

const FRAGMENT_LAYOUT_CARD_CAROUSEL = gql`
  ${FRAGMENTS_CARDS}

  fragment LayoutCardCarousel on LayoutCardCarousel {
    __typename
    id
    cards {
      ...CardPublication
      ...CardProject
      ...CardImageText
      ...CardFileDownload
      ...CardSwissBillQrCodeDonation
    }
  }
`

const FRAGMENT_LAYOUT_TESTIMONIALS = gql`
  ${FRAGMENT_CMP_TESTIMONIAL}

  fragment LayoutTestimonials on LayoutTestimonials {
    __typename
    id
    interval
    testimonials {
      ...CmpTestimonial
    }
  }
`

export const FRAGMENTS_LAYOUTS = gql`
  ${FRAGMENT_LAYOUT_ACCORDION}
  ${FRAGMENT_LAYOUT_GALLERY}
  ${FRAGMENT_LAYOUT_CARDS_GRID}
  ${FRAGMENT_LAYOUT_CARD_CAROUSEL}
  ${FRAGMENT_LAYOUT_TESTIMONIALS}
`

// Section fragments
const FRAGMENT_SECTION_BASIC = gql`
  ${FRAGMENT_SECTION_DESIGN}
  ${FRAGMENTS_LAYOUTS}
  ${FRAGMENTS_CARDS}
  ${FRAGMENT_CMP_CTA}
  ${FRAGMENT_CMP_TITLE}
  ${FRAGMENT_CMP_RICH_TEXT}
  ${FRAGMENT_CMP_IMAGE}
  ${FRAGMENT_CMP_VIDEO}
  ${FRAGMENT_CMP_TESTIMONIAL}
  ${FRAGMENT_CMP_IMG_CAROUSEL}
  ${FRAGMENT_CMP_LATEST_NEWS}
  ${FRAGMENT_CMP_NEWSLETTER_SUBSCRIPTION}

  fragment SectionBasic on SectionBasic {
    __typename
    id
    alignment
    design {
      ...SectionDesign
    }
    content {
      ...LayoutAccordion
      ...LayoutGallery
      ...LayoutCardsGrid
      ...LayoutCardCarousel
      ...LayoutTestimonials

      ...CmpTitle
      ...CmpRichText
      ...CmpImage
      ...CmpVideo
      ...CmpTestimonial
      ...CmpImgCarousel
      ...CmpLatestNews
      ...CmpNewsletterSubscription
      ...CmpCta
    }
  }
`

const FRAGMENT_SECTION_BOXED_TEXT = gql`
  ${FRAGMENT_SECTION_DESIGN}

  fragment SectionBoxedText on SectionBoxedText {
    __typename
    id
    text
    design {
      ...SectionDesign
    }
  }
`

const FRAGMENT_SECTION_CONTACT = gql`
  ${FRAGMENT_SECTION_DESIGN}

  fragment SectionContact on SectionContact {
    __typename
    id
    title
    intro
    address
    inputAppearance
    design {
      ...SectionDesign
    }
  }
`

const FRAGMENT_SECTION_CONTENT_MEDIA = gql`
  ${FRAGMENT_SECTION_DESIGN}
  ${FRAGMENTS_CARDS}
  ${FRAGMENT_CMP_IMAGE}
  ${FRAGMENT_CMP_VIDEO}
  ${FRAGMENT_CMP_TESTIMONIAL}
  ${FRAGMENT_CMP_IMG_CAROUSEL}
  ${FRAGMENT_CMP_TITLE}
  ${FRAGMENT_CMP_RICH_TEXT}
  ${FRAGMENT_CMP_CTA}
  ${FRAGMENT_CMP_LATEST_NEWS}
  ${FRAGMENT_CMP_NEWSLETTER_SUBSCRIPTION}

  fragment SectionContentMedia on SectionContentMedia {
    __typename
    id
    mediaPosition
    contentAlignment
    design {
      ...SectionDesign
    }
    media {
      ...CardProject
      ...CardPublication
      ...CardImageText
      ...CardFileDownload
      ...CardSwissBillQrCodeDonation

      ...CmpImage
      ...CmpVideo
      ...CmpTestimonial
      ...CmpImgCarousel
    }
    otherContent {
      ...CmpCta
      ...CmpImage
      ...CmpImgCarousel
      ...CmpLatestNews
      ...CmpTestimonial
      ...CmpTitle
      ...CmpRichText
      ...CmpNewsletterSubscription
      ...CmpVideo
    }
  }
`

const FRAGMENT_SECTION_CONTENT_SPLIT = gql`
  ${FRAGMENT_SECTION_DESIGN}
  ${FRAGMENT_MEDIA}
  ${FRAGMENT_CMP_CTA}
  ${FRAGMENT_CMP_IMAGE}
  ${FRAGMENT_CMP_IMG_CAROUSEL}
  ${FRAGMENT_CMP_LATEST_NEWS}
  ${FRAGMENT_CMP_NEWSLETTER_SUBSCRIPTION}
  ${FRAGMENT_CMP_RICH_TEXT}
  ${FRAGMENT_CMP_TESTIMONIAL}
  ${FRAGMENT_CMP_TITLE}
  ${FRAGMENT_CMP_VIDEO}
  fragment SectionContentSplit on SectionContentSplit {
    __typename
    id
    imagePosition
    design {
      ...SectionDesign
    }
    image {
      ...Media
    }
    splitContent {
      ...CmpCta
      ...CmpImage
      ...CmpImgCarousel
      ...CmpLatestNews
      ...CmpNewsletterSubscription
      ...CmpRichText
      ...CmpTestimonial
      ...CmpTitle
      ...CmpVideo
    }
  }
`

const FRAGMENT_SECTION_FILTER_ITEMS = gql`
  ${FRAGMENT_SECTION_DESIGN}
  fragment SectionFilterItems on SectionFilterItems {
    __typename
    id
    filter
    showPagination
    showCategoryFilter
    itemsPerPage
    design {
      ...SectionDesign
    }
    categories
  }
`

export const FRAGMENTS_SECTIONS = gql`
  ${FRAGMENT_SECTION_BASIC}
  ${FRAGMENT_SECTION_BOXED_TEXT}
  ${FRAGMENT_SECTION_CONTACT}
  ${FRAGMENT_SECTION_CONTENT_MEDIA}
  ${FRAGMENT_SECTION_CONTENT_SPLIT}
  ${FRAGMENT_SECTION_FILTER_ITEMS}
`

// Hero fragments
const FRAGMENT_HERO_SPLIT = gql`
  ${FRAGMENT_MEDIA}

  fragment HeroSplit on HeroSplit {
    __typename
    id
    title
    intro
    image {
      ...Media
    }
    imagePosition
    splitMinHeight
  }
`

const FRAGMENT_HERO_OVERLAY = gql`
  ${FRAGMENT_MEDIA}
  ${FRAGMENT_CMP_CTA}

  fragment HeroOverlay on HeroOverlay {
    __typename
    id
    title
    intro
    image {
      ...Media
    }
    cta {
      ...CmpCta
    }
    containerWidth
    overlayMinHeight
  }
`

export const FRAGMENTS_HEROES = gql`
  ${FRAGMENT_HERO_SPLIT}
  ${FRAGMENT_HERO_OVERLAY}
`
